<template>
    <div v-if="templatePolicy" class="role-banner">
        <div class="role-chip">
            <i v-if="showLinkedIcon && isInherited" class="ag-icon ag-icon_connection role-chip-icon"></i>
            <i v-if="showLinkedIcon && !isInherited" class="ag-icon ag-icon_unlink role-chip-icon"></i>
            <span class="role-title">Role:</span>
            <span class="role-value">{{ displayRole }}</span>
            <span class="role-chip-notch" aria-hidden="true"></span>
        </div>
        <div class="role-info">
            <i class="ag-icon ag-icon_info_filled role-info-icon"></i>
            <span class="role-info-text">{{ bannerMessage }}</span>
        </div>
    </div>
    <div v-else class="role-banner2">
        <span class="role-text"><span class="role-title">Role:</span> {{ displayRole }}</span>
        <i v-if="showLinkedIcon && isInherited" class="ag-icon ag-icon_connection"
            style="width: 12px; margin-left: 10px;"></i>
        <i v-if="showLinkedIcon && !isInherited" class="ag-icon ag-icon_unlink"
            style="width: 12px; margin-left: 10px;"></i>
    </div>
</template>
<script setup>

import { defineProps, computed, onMounted, watch } from 'vue'
import { store } from '@/store';
import { useRoute } from 'vue-router';
import { GeneralActionTypes } from '@/store/modules/general/action-types';
const props = defineProps({
    currentRole: {
        type: String,
        default: ''
    },
    showLinkedIcon: {
        type: Boolean,
        default: false
    },
    templatePolicy: {
        type: Boolean,
        default: false
    }
})

const route = useRoute();
const inheritanceInfo = computed(() => store.state.general.inheritanceInfo || {});
const isInherited = computed(() => inheritanceInfo.value?.isInherited);
const displayRole = computed(() =>
    (props.currentRole && props.currentRole.trim())
        ? props.currentRole
        : (inheritanceInfo.value?.templateName || '')
);

async function refreshInheritanceInfo() {
    try {
        const name = String(route.name || '');
        const groupLs = localStorage.getItem('group');
        const groupId = groupLs ? (JSON.parse(groupLs)?.mainid ?? null) : null;

        if (name === 'appGroupDetail' || name === 'AppGroupManagement') {
            const appGroupId = route.query?.id;
            if (appGroupId) {
                await store.dispatch(GeneralActionTypes.GET_APPGROUP_INHERITANCE_INFO, { appGroupId });
                return;
            }
            if (groupId) {
                await store.dispatch(GeneralActionTypes.GET_APPGROUP_INHERITANCE_INFO, { groupId });
                return;
            }
        }

        if (name === 'globalPolicyManagement' || name === 'templateGlobalPolicy') {
            if (groupId) {
                await store.dispatch(GeneralActionTypes.GET_GLOBAL_POLICY_INHERITANCE_INFO, groupId);
                return;
            }
        }

        if (name === 'fileTypesPolicy' || name === 'templateFileTypesPolicy') {
            if (groupId) {
                await store.dispatch(GeneralActionTypes.GET_FILE_TYPES_POLICY_INHERITANCE_INFO, groupId);
                return;
            }
        }
    } catch {
        // Non-blocking for banner
    }
}
const bannerMessage = computed(() => {
    const routeName = route.name;
    if (routeName === 'templateAppGroupManagement' || routeName === 'templateAppGroupDetail') {
        return 'Changes made here apply to all App Groups associated with this Role Policy.';
    } else if (routeName === 'templateGlobalPolicy') {
        return 'Changes made here apply to all Global Policies associated with this Role Policy.';
    } else if (routeName === 'templateFileTypesPolicy') {
        return 'Changes made here apply to all File Types Policies associated with this Role Policy.';
    }
    return '';
});

onMounted(async () => {
    if (!inheritanceInfo.value || Object.keys(inheritanceInfo.value).length === 0) {
        await refreshInheritanceInfo();
    }
});

watch(() => route.fullPath, async () => {
    await refreshInheritanceInfo();
});

watch(() => route.query.id, async () => {
    await refreshInheritanceInfo();
});
</script>
<style>
.role-banner {
    position: relative;
    margin-left: calc(-1 * (var(--bs-gutter-x, 15px) + 5px));
    margin-right: calc(-1 * var(--bs-gutter-x, 15px));
    width: calc(100% + calc(var(--bs-gutter-x, 15px) * 2));
    height: 34px;
    display: flex;
    align-items: stretch;
    background: #0C4F93;
    color: #fff;
    z-index: 1030;
    font-size: 0.75rem;
    box-sizing: border-box;
    overflow: hidden;
    border-bottom: 1px solid #d7e3f3;
}

.role-banner2 {
    position: relative;
    margin-left: calc(-1 * (var(--bs-gutter-x, 15px) + 5px));
    margin-right: calc(-1 * var(--bs-gutter-x, 15px));
    width: calc(100% + calc(var(--bs-gutter-x, 15px) * 2));
    height: 34px;
    display: flex;
    z-index: 1030;
    font-size: 0.75rem;
    box-sizing: border-box;
    overflow: hidden;
    align-items: center;
    padding: 0 12px;
    background: #eaf2fb;
    color: #4B7CB3;
    border-bottom: 1px solid #d7e3f3;
}

.ag-sidebar_open main .role-banner {
    margin-left: calc(-1 * (var(--bs-gutter-x, 15px) + 15px));
    width: calc(100% + calc(var(--bs-gutter-x, 15px) * 2) + 25px);
}

.role-chip {
    position: relative;
    display: inline-flex;
    align-items: center;
    height: 100%;
    background: #eaf2fb;
    color: #2b5f98;
    padding: 0 16px 0 24px;
}

.role-chip-icon {
    width: 12px;
    min-width: 12px;
    margin-right: 8px;
}

.role-title {
    font-weight: 700;
    margin-right: 6px;
}

.role-value {
    font-weight: 500;
}

.role-chip-notch {
    position: absolute;
    right: -12px;
    top: 0;
    width: 0;
    height: 0;
    border-top: 17px solid transparent;
    border-bottom: 17px solid transparent;
    border-left: 12px solid #eaf2fb;
}

.role-info {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 12px 0 20px;
    color: #ffffff;
    flex: 1 1 auto;
    overflow: hidden;
}

.role-info-icon {
    width: 14px;
    min-width: 14px;
    filter: brightness(0) invert(1);
}

.role-info-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: 600;
}
</style>